import { OnDestroy, Directive } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { List } from 'immutable'
import { merge, Subscription, Observable } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { Tag, Arn } from 'src/utils/types'
import {
  ResourceSelections,
  resourceSelectionsAsSets,
  UserSelectableResource,
} from './app-resources-selector/app-resources-selector.component'
import { SelectableApplicationResource } from './resource-selector-base'
import { Application, AllRule, ResourceTypeRule, ResourceGroupRule } from '../account/services/application.service'
import { ApiService } from '../services/api.service'
import { ApplicationType } from '../services/dtos'

@Directive()
export abstract class ApplicationFormBase implements OnDestroy {
  protected formChangeSubscription: Subscription = null
  protected _resources: List<UserSelectableResource> = List([])
  protected _sourceResourcesLoaded = false
  filterText = ''

  includeRulesOpen = true
  excludeRulesOpen = false
  exclusionRulesEnabled = false

  constructor(protected apiService: ApiService) {}

  abstract get applicationFormModel(): UntypedFormGroup
  abstract get applicationType(): ApplicationType | ''

  protected get matchingRules(): Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule> {
    return this.applicationFormModel.get('matchingRules').value
  }

  protected get excludingRules(): Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule> {
    return this.applicationFormModel.get('excludingRules').value
  }

  protected get resourceSelections(): ResourceSelections {
    return this.applicationFormModel.get('resourceSelections').value as ResourceSelections
  }

  get resources(): List<UserSelectableResource> {
    return this._resources
  }

  // Returns null while resources haven't loaded, empty Map when loaded with no tags
  get possibleTagValues(): Map<string, Set<string>> | null {
    if (this._resources.size === 0 && !this._sourceResourcesLoaded) return null
    const possibleTagValues = new Map<string, Set<string>>()
    for (const resource of this.resources) {
      resource.tags.forEach((tagVal, tagName) => {
        if (!possibleTagValues.has(tagName)) {
          possibleTagValues.set(tagName, new Set<string>())
        }
        possibleTagValues.get(tagName).add(tagVal)
      })
    }

    // Sort the map keys and create a new sorted map
    const sortedMap = new Map<string, Set<string>>()
    const sortedKeys = Array.from(possibleTagValues.keys()).sort((a, b) => a.localeCompare(b))
    for (const key of sortedKeys) {
      // Sort the values in each set as well
      const sortedValues = Array.from(possibleTagValues.get(key)).sort((a, b) => a.localeCompare(b))
      sortedMap.set(key, new Set(sortedValues))
    }

    return sortedMap
  }

  get possibleResourceTypes(): Observable<Array<{ value: string; displayName: string }>> {
    return this.apiService.getResourceTypes(this.applicationType || undefined)
  }

  get possibleResourceGroups(): Array<{ value: string; displayName: string }> {
    const resourceGroupSet = new Set<string>()
    for (const resource of this.resources) {
      const resourceGroup = resource.resourceGroup
      if (resourceGroup) {
        resourceGroupSet.add(resourceGroup)
      }
    }

    // Convert to array and sort
    const sortedResourceGroups = Array.from(resourceGroupSet).sort((a, b) => a.localeCompare(b))
    return sortedResourceGroups.map((rg) => ({ value: rg, displayName: rg }))
  }

  toggleIncludeConditions(): void {
    this.includeRulesOpen = !this.includeRulesOpen
  }

  toggleExcludeConditions(): void {
    this.excludeRulesOpen = !this.excludeRulesOpen
  }

  toggleExclusionRules(): void {
    this.exclusionRulesEnabled = !this.exclusionRulesEnabled
    // When enabling exclusion rules, also expand the section for convenience
    if (this.exclusionRulesEnabled) {
      this.excludeRulesOpen = true
    }
  }

  get shouldObscureExclusionRules(): boolean {
    return !this.exclusionRulesEnabled
  }

  doFilterResources(event: string): void {
    this.filterText = event
  }

  protected initializeFormChangeSubscription(): void {
    const formChanges = [
      this.applicationFormModel.get('matchingRules').valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.applicationFormModel.get('excludingRules').valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.applicationFormModel.get('resourceSelections').valueChanges,
    ]

    // Add applicationIntegrationIds if it exists
    if (this.applicationFormModel.get('applicationIntegrationIds')) {
      formChanges.push(this.applicationFormModel.get('applicationIntegrationIds').valueChanges)
    }

    this.formChangeSubscription = merge(...formChanges).subscribe(() => {
      this.updateResources()
    })
  }

  protected updateResourceSelection(
    resources: any[],
    matchingArns: Set<Arn>,
    excludedWeakArns: Set<Arn>,
    matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>
  ): void {
    const applicationResources = resources.filter(() => true).map((res) => new SelectableApplicationResource(res))
    Application.updateResourceRuleSelection(
      matchingArns,
      excludedWeakArns,
      matchingRules,
      excludingRules,
      applicationResources
    )
    this._resources = List(applicationResources)
  }

  protected getResourceSelectionSets(): { selectedResources: Set<Arn>; suppressedResources: Set<Arn> } {
    return resourceSelectionsAsSets(this.resourceSelections)
  }

  abstract updateResources(): void

  ngOnDestroy(): void {
    if (subscriptionActive(this.formChangeSubscription)) {
      this.formChangeSubscription.unsubscribe()
    }
    this.formChangeSubscription = null
  }
}
